(() => {
  'use strict';

  const ELEMENT_NAME = 'ldx-sakazuki-single-video-hero';
  if (customElements.get(ELEMENT_NAME)) return;

  const STATES = Object.freeze({
    BOOT: 'BOOT',
    MEDIA_LOADING: 'MEDIA_LOADING',
    MEDIA_READY: 'MEDIA_READY',
    VIDEO_PLAYING: 'VIDEO_PLAYING',
    POSTER_READY: 'POSTER_READY',
    COLLECTION_READY: 'COLLECTION_READY',
  });

  class LdxSakazukiSingleVideoHero extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.video = this.querySelector('[data-ldx-single-video]');
      this.environmentVideo = this.querySelector('[data-ldx-environment-video]');
      this.editorMode = Boolean(window.Shopify?.designMode);
      this.toggleAttribute('data-editor-preview', this.editorMode);
      this.sequence = this.querySelector('[data-ldx-collection-sequence]');
      this.wordmark = this.querySelector('.ldx-collection-sequence__wordmark');
      this.flashes = Array.from(this.querySelectorAll('[data-ldx-flash]'))
        .filter((node) => node.textContent.trim());
      this.category = this.querySelector('[data-ldx-category-lockup]') || this.querySelector('.ldx-collection-sequence__category');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.headerThemeBefore = document.documentElement.getAttribute('data-r7-header-theme');
      this.shellSnapshots = new Map();
      this.menuOpen = Boolean(document.querySelector('#Details-menu-drawer-container[open]'));
      this.documentHidden = document.hidden;
      this.closingVisible = false;
      this.sequenceStarted = false;
      this.collectionReady = false;
      this.mediaReady = false;
      this.mediaFailed = false;
      this.mediaTimeout = 0;
      this.environmentPrepared = false;

      this.prepareFollowup();
      this.captureShellState();
      this.setState(STATES.BOOT);
      this.setHeaderTheme('dark');
      this.bindEvents();
      this.setupOpeningObserver();
      this.setupClosingObserver();
      this.prepareVideo();

      requestAnimationFrame(() => this.boot());
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;
      this.abortController.abort();
      window.clearTimeout(this.mediaTimeout);
      this.timeline?.kill();
      this.timeline = null;
      this.openingObserver?.disconnect();
      this.closingObserver?.disconnect();
      this.video?.pause();
      this.environmentVideo?.pause();
      if (document.body.dataset.r7CollectionLoopActive === (this.dataset.sectionId || 'collection')) {
        delete document.body.dataset.r7CollectionLoopActive;
      }
      this.cleanupFollowupMarker();
      this.restoreShellState();
      if (this.headerThemeBefore === null) document.documentElement.removeAttribute('data-r7-header-theme');
      else document.documentElement.dataset.r7HeaderTheme = this.headerThemeBefore;
    }

    bindEvents() {
      document.addEventListener('visibilitychange', () => {
        this.documentHidden = document.hidden;
        this.syncPlayback();
      }, { signal: this.signal });

      document.addEventListener('ld:menu-state', (event) => {
        this.menuOpen = Boolean(event.detail?.open);
        if (this.menuOpen) this.timeline?.pause();
        else if (!this.reduceMotionQuery.matches && !this.collectionReady) this.timeline?.resume();
        this.syncPlayback();
      }, { signal: this.signal });

      document.addEventListener('shopify:section:select', (event) => {
        const wrapper = this.closest('.shopify-section');
        if (event.target === wrapper || event.target?.contains?.(this)) {
          this.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }, { signal: this.signal });

      this.reduceMotionQuery.addEventListener?.('change', (event) => {
        if (!event.matches || this.collectionReady) return;
        this.timeline?.kill();
        this.timeline = null;
        this.revealCollection('reduced-motion');
      }, { signal: this.signal });

      this.video?.addEventListener('loadeddata', () => this.onMediaReady(), { signal: this.signal });
      this.video?.addEventListener('canplay', () => this.onMediaReady(), { signal: this.signal });
      this.video?.addEventListener('error', () => this.onMediaError('video-error'), { signal: this.signal });
      this.environmentVideo?.addEventListener('playing', () => {
        this.dataset.environmentState = 'playing';
      }, { signal: this.signal });
      this.environmentVideo?.addEventListener('pause', () => {
        if (this.dataset.environmentState === 'playing') this.dataset.environmentState = 'paused';
      }, { signal: this.signal });
      this.environmentVideo?.addEventListener('error', () => {
        this.dataset.environmentState = 'poster';
        this.dataset.environmentWarning = 'media-error';
      }, { signal: this.signal });
    }

    prepareFollowup() {
      if (this.editorMode) return;
      const wrapper = this.closest('.shopify-section');
      this.followup = wrapper?.nextElementSibling || this.parentElement?.nextElementSibling || null;
      if (!this.followup) return;
      this.followupOwner = `${this.dataset.sectionId || 'opening'}-${Math.random().toString(36).slice(2)}`;
      this.followup.dataset.ldxOpeningFollowup = this.followupOwner;
      this.followup.dataset.ldxOpeningReveal = 'false';
    }

    cleanupFollowupMarker() {
      if (!this.followup || this.followup.dataset.ldxOpeningFollowup !== this.followupOwner) return;
      delete this.followup.dataset.ldxOpeningFollowup;
      delete this.followup.dataset.ldxOpeningReveal;
    }

    captureShellState() {
      document.querySelectorAll('[data-ld-floating-shortcuts], .ld-r3-header-section').forEach((element) => {
        if (this.shellSnapshots.has(element)) return;
        this.shellSnapshots.set(element, {
          ariaHidden: element.getAttribute('aria-hidden'),
          inert: element.hasAttribute('inert'),
        });
      });
    }

    syncShellState() {
      if (this.editorMode) return;
      const owner = this.dataset.sectionId || this.id || 'collection-opening';
      if (!this.collectionReady) {
        document.body.dataset.r7CollectionOpening = owner;
        this.shellSnapshots.forEach((_snapshot, element) => {
          element.setAttribute('aria-hidden', 'true');
          element.setAttribute('inert', '');
        });
        return;
      }
      if (document.body.dataset.r7CollectionOpening === owner) delete document.body.dataset.r7CollectionOpening;
      this.restoreShellElements();
    }

    restoreShellElements() {
      this.shellSnapshots.forEach((snapshot, element) => {
        if (!element.isConnected) return;
        if (snapshot.ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', snapshot.ariaHidden);
        element.toggleAttribute('inert', snapshot.inert);
      });
    }

    restoreShellState() {
      const owner = this.dataset.sectionId || this.id || 'collection-opening';
      if (document.body.dataset.r7CollectionOpening === owner) delete document.body.dataset.r7CollectionOpening;
      this.restoreShellElements();
      this.shellSnapshots.clear();
    }

    prepareVideo() {
      if (!this.video) return;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.autoplay = true;
      this.video.loop = true;
      this.video.preload = 'metadata';
      this.video.defaultPlaybackRate = 1;
      this.video.playbackRate = 1;
      if (this.editorMode) {
        this.video.autoplay = false;
        this.video.controls = true;
        this.video.removeAttribute('aria-hidden');
        this.video.closest('.ldx-sakazuki-single-video-hero__media')?.removeAttribute('aria-hidden');
        this.prepareEnvironmentVideo({ editorPreview: true });
      }
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        if (source.dataset.type) source.type = source.dataset.type;
      });
      if (this.video.querySelector('source[src]')) this.video.load();
    }

    prepareEnvironmentVideo({ editorPreview = false } = {}) {
      if (!this.environmentVideo || this.environmentPrepared) return;
      this.environmentPrepared = true;
      this.environmentVideo.muted = true;
      this.environmentVideo.defaultMuted = true;
      this.environmentVideo.playsInline = true;
      this.environmentVideo.loop = true;
      this.environmentVideo.preload = editorPreview ? 'metadata' : 'auto';
      if (editorPreview) {
        this.dataset.editorEnvironment = 'true';
        this.environmentVideo.controls = true;
        this.environmentVideo.removeAttribute('aria-hidden');
        this.environmentVideo.removeAttribute('tabindex');
        this.environmentVideo.closest('.ldx-sakazuki-single-video-hero__media')?.removeAttribute('aria-hidden');
      }
      this.environmentVideo.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        if (source.dataset.type) source.type = source.dataset.type;
      });
      if (this.environmentVideo.querySelector('source[src]')) this.environmentVideo.load();
      this.dataset.environmentState = 'poster';
    }

    boot() {
      if (!this.connected) return;
      if (this.reduceMotionQuery.matches) {
        this.setState(STATES.POSTER_READY);
        this.revealCollection('reduced-motion');
        return;
      }
      if (!this.video || !this.video.querySelector('source[src]')) {
        this.onMediaError('missing-video');
        return;
      }
      this.setState(STATES.MEDIA_LOADING);
      if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) this.onMediaReady();
      else this.mediaTimeout = window.setTimeout(() => this.onMediaError('media-timeout'), 6000);
    }

    onMediaReady() {
      if (this.mediaReady) return;
      this.mediaReady = true;
      this.mediaFailed = false;
      this.video?.removeAttribute('data-playback-disabled');
      delete this.dataset.mediaError;
      window.clearTimeout(this.mediaTimeout);
      if (this.collectionReady) {
        this.syncPlayback();
        return;
      }
      this.setState(STATES.MEDIA_READY);
      if (this.editorMode) return;
      this.syncPlayback();
      this.startOpeningSequence();
    }

    onMediaError(reason) {
      if (this.mediaFailed || this.collectionReady) return;
      this.mediaFailed = true;
      window.clearTimeout(this.mediaTimeout);
      this.dataset.mediaError = reason;
      if (this.video) {
        this.video.pause();
        this.video.dataset.playbackDisabled = 'true';
      }
      this.setState(STATES.POSTER_READY);
      if (this.editorMode) return;
      this.startOpeningSequence();
    }

    waitForGsap(attempt = 0) {
      if (!this.connected || this.sequenceStarted || this.collectionReady) return;
      if (window.gsap) {
        this.buildOpeningTimeline();
        return;
      }
      if (attempt >= 120) {
        this.revealCollection('gsap-unavailable');
        return;
      }
      window.setTimeout(() => this.waitForGsap(attempt + 1), 50);
    }

    startOpeningSequence() {
      if (this.sequenceStarted || this.collectionReady) return;
      if (!this.sequence || this.reduceMotionQuery.matches) {
        this.revealCollection('static-opening');
        return;
      }
      this.waitForGsap();
    }

    buildOpeningTimeline() {
      if (this.sequenceStarted || this.collectionReady || !window.gsap) return;
      this.sequenceStarted = true;
      const gsap = window.gsap;
      const timeline = gsap.timeline({ paused: true, onComplete: () => this.revealCollection('sequence-complete') });
      this.timeline = timeline;

      gsap.set([this.wordmark, ...this.flashes, this.category], { autoAlpha: 0 });
      this.sequence.dataset.sequenceState = 'BLACK';

      timeline
        .call(() => { this.sequence.dataset.sequenceState = 'BRAND'; })
        .fromTo(this.wordmark, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: .65, ease: 'power3.out' })
        .to(this.wordmark, { autoAlpha: 1, duration: .7 })
        .to(this.wordmark, { autoAlpha: 0, y: -12, duration: .3, ease: 'power2.in' });

      this.flashes.forEach((flash) => {
        timeline
          .call(() => { this.sequence.dataset.sequenceState = 'FLASH'; })
          .fromTo(flash, { autoAlpha: 0, x: '-22vw', filter: 'blur(7px)' }, {
            autoAlpha: 1,
            x: 0,
            filter: 'blur(0px)',
            duration: .62,
            ease: 'power3.out',
          })
          .to(flash, { autoAlpha: 1, duration: .78 })
          .to(flash, { autoAlpha: 0, x: '9vw', filter: 'blur(4px)', duration: .34, ease: 'power2.in' })
          .to({}, { duration: .08 });
      });

      timeline
        .call(() => { this.sequence.dataset.sequenceState = 'CATEGORY'; })
        .fromTo(this.category, { autoAlpha: 0, scale: .9, y: 26 }, {
          autoAlpha: 1,
          scale: 1,
          y: 0,
          duration: .82,
          ease: 'power3.out',
        })
        .to(this.category, { autoAlpha: 1, duration: 1.1 })
        .call(() => { this.sequence.dataset.sequenceState = 'EXIT'; })
        .to(this.sequence, { autoAlpha: 0, duration: .5, ease: 'power2.inOut' });

      if (!this.menuOpen && !this.documentHidden) timeline.play();
    }

    revealCollection(reason) {
      if (this.editorMode) return;
      if (this.collectionReady) return;
      this.collectionReady = true;
      this.dataset.entryReason = reason;
      this.prepareEnvironmentVideo();
      this.sequence?.setAttribute('data-sequence-state', 'EXIT');
      if (this.followup?.dataset.ldxOpeningFollowup === this.followupOwner) {
        this.followup.dataset.ldxOpeningReveal = 'true';
      }
      this.setState(STATES.COLLECTION_READY);
      document.body.dataset.r7CollectionLoopActive = this.dataset.sectionId || 'collection';
      this.setHeaderTheme('light');
      this.syncPlayback();
      requestAnimationFrame(() => {
        if (!this.connected) return;
        this.followup?.scrollIntoView({ behavior: 'auto', block: 'start' });
        window.setTimeout(() => {
          if (!this.connected) return;
          this.cleanupFollowupMarker();
          window.ScrollTrigger?.refresh();
        }, 560);
      });
    }

    syncPlayback() {
      if (this.editorMode) return;
      const activeVideo = this.collectionReady && this.environmentVideo ? this.environmentVideo : this.video;
      const inactiveVideo = activeVideo === this.environmentVideo ? this.video : this.environmentVideo;
      inactiveVideo?.pause();
      if (!activeVideo || this.reduceMotionQuery.matches) {
        activeVideo?.pause();
        return;
      }
      if (activeVideo === this.video && this.mediaFailed) return;
      if (activeVideo === this.environmentVideo && !this.environmentPrepared) this.prepareEnvironmentVideo();
      const shouldPlay = !this.menuOpen && !this.documentHidden && !this.closingVisible &&
        (activeVideo === this.environmentVideo || this.mediaReady || this.collectionReady);
      if (!shouldPlay) {
        activeVideo.pause();
        return;
      }
      activeVideo.loop = true;
      activeVideo.muted = true;
      activeVideo.defaultMuted = true;
      activeVideo.playsInline = true;
      activeVideo.playbackRate = 1;
      const playResult = activeVideo.play();
      playResult?.then?.(() => {
        if (!this.collectionReady) this.setState(STATES.VIDEO_PLAYING);
        else if (activeVideo === this.environmentVideo) this.dataset.environmentState = 'playing';
      }).catch?.(() => {
        if (activeVideo === this.environmentVideo) {
          this.dataset.environmentState = 'poster';
          this.dataset.environmentWarning = 'autoplay-rejected';
          return;
        }
        this.dataset.mediaWarning = 'WARNING_AUTOPLAY_REJECTED';
        if (!this.collectionReady) this.onMediaError('play-rejected');
        else {
          this.mediaFailed = true;
          this.video.dataset.playbackDisabled = 'true';
        }
      });
    }

    setupOpeningObserver() {
      if (!('IntersectionObserver' in window)) return;
      this.openingObserver = new IntersectionObserver(([entry]) => {
        const visible = Boolean(entry?.isIntersecting);
        this.setHeaderTheme(visible && !this.collectionReady ? 'dark' : 'light');
        if (!this.collectionReady && !visible) this.video?.pause();
        else this.syncPlayback();
      }, { threshold: .01 });
      this.openingObserver.observe(this);
    }

    setupClosingObserver() {
      const closing = document.querySelector('.r7-collection-closing');
      if (!closing || !('IntersectionObserver' in window)) return;
      this.closingObserver = new IntersectionObserver(([entry]) => {
        this.closingVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .5);
        if (this.closingVisible) this.dataset.environmentFading = 'true';
        else delete this.dataset.environmentFading;
        this.syncPlayback();
      }, { threshold: [0, .5, 1] });
      this.closingObserver.observe(closing);
    }

    setHeaderTheme(theme) {
      document.documentElement.dataset.r7HeaderTheme = theme === 'dark' ? 'dark' : 'light';
    }

    setState(state) {
      this.state = state;
      this.dataset.openingState = state;
      this.dataset.openingReady = String(state !== STATES.BOOT && state !== STATES.MEDIA_LOADING);
      this.syncShellState();
      this.dispatchEvent(new CustomEvent('ldx:collection-opening-state', {
        bubbles: true,
        detail: { sectionId: this.dataset.sectionId || '', state },
      }));
    }
  }

  customElements.define(ELEMENT_NAME, LdxSakazukiSingleVideoHero);
})();
